import { useCallback, useEffect, useRef } from 'react';
import { NavigateOptions, To, useNavigate } from 'react-router';

import { StableNavigateContext } from './stableNavigateContext';

/**
 * The default navigate function from react-router will cause component re-renders
 * when location state changes, which is not desired for a function which should not
 * have location dependencies. In some cases this can cause multiple expensive re-renders
 * of components which are not dependent on the location state.
 *
 * This context provides a stable navigate function which can be used to navigate
 * without causing re-renders of components which are not dependent on the location state.
 * Should be used everywhere instead of the default navigate function from react-router.
 */

export function StableNavigateProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const navigate = useNavigate();

  const navigateRef = useRef(navigate);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const stableNavigate = useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        navigateRef.current(to);
      } else {
        navigateRef.current(to, options);
      }
    },
    []
  );

  return (
    <StableNavigateContext.Provider value={stableNavigate}>
      {children}
    </StableNavigateContext.Provider>
  );
}
